import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, MessageCircle, Ghost, Truck, LocateFixed, Loader2, AlertTriangle, Crown, Gift } from "lucide-react";
import { toast } from "sonner";
import { api, imageUrl, whatsappLink, snapchatLink } from "../lib/api";
import { track } from "../lib/analytics";
import { FreeDeliveryBar } from "../components/FreeDeliveryBar";
import { CartCrossSell } from "../components/CartCrossSell";
import { useCart } from "../context/CartContext";
import { useI18n } from "../i18n";

const PLACEHOLDER = "https://customer-assets.emergentagent.com/job_store-hub-157/artifacts/yyc18991_IMG_4514.jpeg";

function haversine(a, b) {
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLon = ((b[1] - a[1]) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos((a[0] * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

const isValidPhone = (raw) => {
  const d = (raw || "").replace(/[^\d+]/g, "");
  return /^(?:\+33|0033|33|0)[1-9]\d{8}$/.test(d) || /^(?:\+32|0032|32)[1-9]\d{7,8}$/.test(d) || /^0[1-9]\d{7,8}$/.test(d);
};

export default function Checkout() {
  useEffect(() => { track("checkout_view"); }, []);
  const { items, vendor, total, setQty, removeItem, clear, addItem } = useCart();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_address: "", notes: "", door_code: "" });
  const [instruction, setInstruction] = useState("");
  const [cashAmount, setCashAmount] = useState("");
  const [payMethod, setPayMethod] = useState("card");
  const [acceptsCard, setAcceptsCard] = useState(false);
  const [snapHandle, setSnapHandle] = useState("");
  const [optionsOpen, setOptionsOpen] = useState(false);
  const isNational = !!vendor?.is_national || vendor?.id === "national";
  const [nationalPay, setNationalPay] = useState(null);
  useEffect(() => { if (isNational) api.get("/settings/national-payment").then((r) => setNationalPay(r.data)).catch(() => {}); }, [isNational]);
  useEffect(() => {
    if (!vendor?.id) return;
    api.get(`/vendors/${vendor.id}`).then((r) => { const ok = !!r.data?.accepts_card; setAcceptsCard(ok); setPayMethod(ok ? "card" : "cash"); }).catch(() => setAcceptsCard(false));
  }, [vendor?.id]);
  const [channel, setChannel] = useState("whatsapp");
  const [submitting, setSubmitting] = useState(false);
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [club, setClub] = useState(null);
  const [clubOptIn, setClubOptIn] = useState(false);
  const [groupingOk, setGroupingOk] = useState(false);
  const [refInput, setRefInput] = useState("");
  const [referral, setReferral] = useState(null);
  const [refLoading, setRefLoading] = useState(false);
  const [refCredit, setRefCredit] = useState(0);
  const [addrStatus, setAddrStatus] = useState("idle");
  const [addrLabel, setAddrLabel] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const skipAddrEffectRef = useRef(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const verifyAddress = async (address) => {
    setAddrStatus("loading");
    try {
      const { data } = await api.post("/geocode", { address, city: vendor?.is_national ? "" : (vendor?.city || "") }, { timeout: 10000 });
      setCoords([data.lat, data.lng]);
      setAddrStatus("ok");
      setAddrLabel(data.label);
    } catch {
      setAddrStatus("error");
      setAddrLabel("");
    }
  };

  const pickSuggestion = (s) => {
    skipAddrEffectRef.current = true;
    setForm((f) => ({ ...f, customer_address: s.label }));
    setCoords([s.lat, s.lng]);
    setAddrStatus("ok");
    setAddrLabel(s.label);
    setSuggestions([]);
  };

  useEffect(() => {
    if (skipAddrEffectRef.current) { skipAddrEffectRef.current = false; return; }
    const address = form.customer_address.trim();
    setAddrStatus("idle");
    setAddrLabel("");
    if (address.length < 3) { setSuggestions([]); return; }
    const sugTimer = setTimeout(() => {
      api.get("/geocode/suggest", { params: { q: address, city: vendor?.is_national ? "" : (vendor?.city || ""), lat: vendor?.lat ?? undefined, lng: vendor?.lng ?? undefined }, timeout: 8000 })
        .then((r) => setSuggestions(Array.isArray(r.data) ? r.data : []))
        .catch(() => setSuggestions([]));
    }, 350);
    const verifyTimer = address.length >= 10 ? setTimeout(() => verifyAddress(address), 1500) : null;
    return () => { clearTimeout(sugTimer); if (verifyTimer) clearTimeout(verifyTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.customer_address]);

  useEffect(() => {
    const phone = form.customer_phone.trim();
    if (phone.length < 8) { setClub(null); return; }
    const timer = setTimeout(() => {
      api.get(`/club/status/${encodeURIComponent(phone)}`).then((r) => setClub(r.data)).catch(() => setClub(null));
    }, 600);
    return () => clearTimeout(timer);
  }, [form.customer_phone]);

  const subtotal = total;
  const base = vendor?.delivery_base ?? 3.5;
  const perKm = vendor?.delivery_per_km ?? 0.8;
  const freeThreshold = vendor?.free_threshold ?? 69;
  const radius = vendor?.radius_km ?? 10;
  const INCLUDED_KM = 2;

  const distance = useMemo(() => {
    if (coords && vendor?.lat != null && vendor?.lng != null) {
      return Math.round(haversine([vendor.lat, vendor.lng], coords) * 10) / 10;
    }
    return null;
  }, [coords, vendor]);

  const clubActive = !!club?.active;
  const clubMin = club?.min_order ?? 69;
  const clubFree = clubActive && subtotal >= clubMin;
  const effectiveThreshold = clubActive ? Math.min(clubMin, freeThreshold) : freeThreshold;
  const clubFee = clubOptIn && !clubActive ? 5.99 : 0;

  const distanceFee = useMemo(() => (distance != null ? Math.round((base + perKm * Math.max(0, distance - INCLUDED_KM)) * 100) / 100 : Math.round(base * 100) / 100), [distance, base, perKm]);
  const deliveryFee = clubFree || subtotal >= freeThreshold ? 0 : distanceFee;

  const outOfZone = distance != null && distance > radius;
  const discount = promo?.free_delivery ? deliveryFee : (promo?.discount || 0);
  const refDiscount = referral ? 5 : 0;
  const refCreditApplied = !referral && !promo && refCredit >= 5 && subtotal >= 20 ? 5 : 0;
  const refCreditBlocked = !referral && !!promo && refCredit >= 5 && subtotal >= 20;
  const grandTotal = Math.round((Math.max(subtotal - discount - refDiscount - refCreditApplied, 0) + deliveryFee + clubFee) * 100) / 100;
  const pointsEarned = Math.floor(Math.max(subtotal - discount, 0));

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoLoading(true);
    try {
      const { data } = await api.post("/promo/validate", {
        code, vendor_id: vendor.id, phone: form.customer_phone.trim(), delivery_fee: deliveryFee,
        items: items.map((i) => ({ product_id: i.product_id, price: i.price, qty: i.qty })),
      });
      if (data.ok && (data.discount > 0 || data.free_delivery)) {
        setPromo(data);
        toast.success(data.free_delivery ? data.message : `${t("co.promoOk")} -${data.discount.toFixed(2)}€${data.was_capped ? " " + t("co.capped") : ""}`, { style: { background: "#121212", color: "#9dff00", border: "1px solid #9dff00" } });
      } else {
        setPromo(null);
        toast.error(data.message || t("co.promoErr"));
      }
    } catch { setPromo(null); toast.error(t("co.promoErr")); }
    finally { setPromoLoading(false); }
  };

  const applyReferral = async () => {
    const code = refInput.trim().toUpperCase();
    if (!code) return;
    setRefLoading(true);
    try {
      const { data } = await api.post("/referral/check", { code, phone: form.customer_phone.trim(), subtotal });
      if (data.ok) {
        setReferral({ code: data.code, discount: data.discount });
        toast.success(t("co.refOk"), { style: { background: "#121212", color: "#9dff00", border: "1px solid #9dff00" } });
      } else {
        setReferral(null);
        toast.error(data.message || t("co.refErr"));
      }
    } catch { setReferral(null); toast.error(t("co.refErr")); }
    finally { setRefLoading(false); }
  };

  useEffect(() => {
    const phone = form.customer_phone.trim();
    if (phone.length < 9) { setRefCredit(0); return; }
    const timer = setTimeout(() => {
      api.get("/referral/status", { params: { phone } }).then((r) => setRefCredit(Number(r.data?.credit) || 0)).catch(() => setRefCredit(0));
    }, 800);
    return () => clearTimeout(timer);
  }, [form.customer_phone]);

  const clearPromo = () => { setPromo(null); setPromoInput(""); };
  const optionsSummary = useMemo(() => [promo && "promo", referral && "parrain", clubOptIn && "club", groupingOk && "regroupement"].filter(Boolean).join(" · "), [promo, referral, clubOptIn, groupingOk]);

  const locate = () => {
    if (!navigator.geolocation) { toast.error(t("co.geoNo")); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords([pos.coords.latitude, pos.coords.longitude]); setLocating(false); toast.success(t("co.geoOk")); },
      () => { setLocating(false); toast.error(t("co.geoDenied")); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const buildMessage = (o) => {
    const payCard = payMethod === "card" && acceptsCard;
    let msg = `*NOUVELLE COMMANDE STUFF* (Ref ${o.ref})\n\n`;
    msg += `Client: ${form.customer_name}\nTel: ${form.customer_phone}\nAdresse: ${form.customer_address}, ${vendor.city}\n\n`;
    msg += `Commande:\n`;
    items.forEach((i) => { msg += `- ${i.qty}x ${i.name} : ${(i.price * i.qty).toFixed(2)} EUR\n`; });
    msg += `\nSous-total: ${o.subtotal.toFixed(2)} EUR`;
    msg += `\nLivraison: ${o.delivery_fee === 0 ? "OFFERTE" : o.delivery_fee.toFixed(2) + " EUR"}`;
    if (o.club_free_delivery) msg += ` (Club STUFF)`;
    if (o.distance_km != null) msg += ` (${o.distance_km} km)`;
    if (o.club_fee > 0) msg += `\nAdhesion CLUB STUFF: +5.99 EUR (a encaisser a la livraison)`;
    if (o.discount > 0) msg += `\nReduction ${o.promo_code}: -${o.discount.toFixed(2)} EUR`;
    if (o.referral_discount > 0) msg += `\nCode parrain ${o.referral_code}: -${o.referral_discount.toFixed(2)} EUR`;
    if (o.referral_credit_used > 0) msg += `\nCredit parrainage: -${o.referral_credit_used.toFixed(2)} EUR`;
    if (o.grouping_ok) msg += `\nRegroupement OK : livraison differee acceptee (petit cadeau du vendeur 🎁)`;
    if (o.out_of_zone) msg += `\n/!\\ HORS ZONE (${o.distance_km} km > ${radius} km) - a confirmer avec le vendeur`;
    msg += `\n*TOTAL A ENCAISSER A LA LIVRAISON : ${o.total.toFixed(2)} EUR*`;
    msg += isNational ? `\nPaiement: VIREMENT INSTANTANE (ref ${o.payment_ref || "STUFF-" + o.ref})` : payCard ? `\nPaiement: CARTE sans contact (terminal du livreur)` : `\nPaiement: ESPECES a la remise`;
    if (channel === "snapchat" && snapHandle) msg += `\nSnapchat client: ${snapHandle}`;
    if (form.door_code) msg += `\n\nDigicode/Acces: ${form.door_code}`;
    if (instruction) msg += `\nConsigne coursier: ${instruction}`;
    if (!payCard && cashAmount) msg += `\nPaiement especes: ${cashAmount}${cashAmount !== "Montant exact" ? " (prevoir la monnaie)" : ""}`;
    if (form.notes) msg += `\n\nNote: ${form.notes}`;
    return msg;
  };

  const contactLink = (msg) => {
    if (channel === "snapchat" && vendor.snapchat) {
      // Snapchat ne pré-remplit pas le message : on le copie dans le presse-papier
      Promise.resolve(navigator.clipboard?.writeText(msg)).catch(console.error);
      return snapchatLink(vendor.snapchat) || whatsappLink(vendor.whatsapp || vendor.phone, msg);
    }
    return whatsappLink(vendor.whatsapp || vendor.phone, msg);
  };

  const writeInterstitial = (win, link) => {
    try {
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>STUFF CBD</title></head>
<body style="background:#0a0a0a;color:#fff;font-family:Arial,sans-serif;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;margin:0;text-align:center;padding:24px;box-sizing:border-box">
<div style="font-size:52px;line-height:1">✅</div>
<h1 style="text-transform:uppercase;color:#9dff00;margin:16px 0 6px;font-size:24px;letter-spacing:1px">${t("os.popupTitle")}</h1>
<p style="color:#999;margin:0 0 28px;font-size:15px">${t("os.popupText")}</p>
<a href="${link}" style="background:#9dff00;color:#000;text-decoration:none;font-weight:bold;text-transform:uppercase;padding:14px 32px;font-size:14px;margin-bottom:14px">${t("os.popupOpen")}</a>
<a href="${window.location.origin}" style="color:#777;text-decoration:underline;font-size:13px">${t("os.popupBack")}</a>
<scr` + `ipt>setTimeout(function(){window.location.href=${JSON.stringify(link)};},700);</scr` + `ipt>
</body></html>`;
      const blobUrl = URL.createObjectURL(new Blob([html], { type: "text/html" }));
      win.location.href = blobUrl;
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
    } catch (err) {
      console.warn("Interstitiel indisponible, redirection directe :", err?.message);
      try { win.location.href = link; } catch (e) { console.error("popup fermée:", e); }
    }
  };

  const [unavailable, setUnavailable] = useState([]);
  const recheckStock = async () => {
    const { data } = await api.post("/orders/check-stock", {
      vendor_id: vendor.id,
      items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, qty: i.qty, tier: i.tier || null })),
    });
    const short = data?.shortages || [];
    if (short.length === 0) return true;
    const gone = short.filter((x) => x.available <= 0);
    gone.forEach((x) => items.filter((i) => i.product_id === x.product_id).forEach((i) => removeItem(i.key || i.product_id)));
    short.filter((x) => x.available > 0).forEach((x) => {
      const line = items.find((i) => i.product_id === x.product_id);
      if (line) setQty(line.key || line.product_id, Math.max(1, Math.floor(x.available / (line.units || 1))));
    });
    setUnavailable(short.map((x) => ({ ...x, gone: x.available <= 0 })));
    toast.error(gone.length ? t("co.unavailable") : `${t("co.stockShort")} ${short.map((x) => `${x.name} : ${x.available}`).join(", ")}`);
    return false;
  };

  useEffect(() => {
    if (!vendor?.id || items.length === 0) return;
    recheckStock().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor?.id]);

  const [errors, setErrors] = useState({});
  const formStartedRef = React.useRef(false);
  const markStarted = () => { if (!formStartedRef.current) { formStartedRef.current = true; track("form_started"); } };
  const validate = () => {
    const errs = {};
    if (form.customer_name.trim().length < 2) errs.customer_name = "Indique ton nom complet";
    if (!isValidPhone(form.customer_phone)) errs.customer_phone = "Numéro invalide — format français (06…) ou belge (+32…)";
    if (form.customer_address.trim().length < 8) errs.customer_address = "Indique ton adresse complète (numéro, rue, code postal, ville)";
    else if (vendor?.is_belgian) { if (!/\d/.test(form.customer_address) || !/\b\d{4}\b/.test(form.customer_address)) errs.customer_address = "Adresse incomplète : numéro, rue, code postal (4 chiffres) et ville (ex : 12 Rue Neuve, 1000 Bruxelles)"; }
    else if (addrStatus !== "ok") errs.customer_address = "Adresse non reconnue : choisis une adresse proposée (numéro, rue, code postal, ville)";
    if (channel === "snapchat" && !isNational && snapHandle.trim().length < 2) errs.snap_handle = "Indique ton pseudo Snapchat pour que le livreur te contacte";
    setErrors(errs);
    const first = Object.keys(errs)[0];
    if (first) {
      track("validation_error", { meta: { field: first } });
      const el = document.querySelector(`[data-field="${first}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => el?.focus?.(), 350);
    }
    return !first;
  };

  const submit = async (e) => {
    e.preventDefault();
    track("confirm_click");
    if (!validate()) return;
    try { if (!(await recheckStock())) return; } catch (err) { console.warn("check-stock indisponible :", err?.message); }
    // Ouvre l'onglet MAINTENANT (geste utilisateur) pour éviter le blocage popup après l'await
    const win = window.open("", "_blank");
    track(channel === "snapchat" ? "snap_click" : "whatsapp_click", { meta: { where: "checkout" } });
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        vendor_id: vendor.id,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_address: form.customer_address,
        city: vendor.city,
        notes: form.notes,
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, qty: i.qty, tier: i.tier || null })),
        customer_lat: coords ? coords[0] : null,
        customer_lng: coords ? coords[1] : null,
        promo_code: promo?.code || "",
        referral_code: referral?.code || "",
        club_signup: clubOptIn && !clubActive,
        grouping_ok: groupingOk,
        door_code: form.door_code,
        courier_instruction: instruction,
        cash_amount: payMethod === "card" && acceptsCard ? "" : cashAmount,
        payment_method: payMethod === "card" && acceptsCard ? "card" : "cash",
        snap_handle: channel === "snapchat" ? snapHandle.trim() : "",
      });
      // Message construit avec les montants CALCULÉS PAR LE SERVEUR (source de vérité)
      const msg = buildMessage(data);
      const link = contactLink(msg);
      if (link && win) {
        writeInterstitial(win, link);
      } else if (win) {
        win.close();
      }
      toast.success(`${t("co.sent1")}${data.points_pending} ${t("co.sent2")}`, { style: { background: "#121212", color: "#9dff00", border: "1px solid #9dff00" } });
      const contact = { name: vendor.name, whatsapp: vendor.whatsapp, snapchat: vendor.snapchat, phone: vendor.phone, city: vendor.city };
      localStorage.setItem("stuff_ordered", "1");
      clear();
      navigate("/commande-envoyee", { state: { ref: data.ref, vendor: contact, channel, message: msg, phone: form.customer_phone, national: isNational, total: data.total, payment_ref: data.payment_ref || `STUFF-${data.ref}` } });
    } catch (err) {
      if (win) win.close();
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) { recheckStock().catch(() => {}); }
      toast.error(Array.isArray(detail) ? detail.map((d) => d?.msg || String(d)).join(", ") : (typeof detail === "string" ? detail : t("co.orderErr")));
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        <img src="/lion-portrait.webp" alt="Mascotte STUFF" className="mx-auto h-40 object-contain opacity-90 mb-4" loading="lazy" />
        <h1 className="font-anton text-3xl uppercase text-white mb-2">{t("co.emptyTitle")}</h1>
        <p className="text-zinc-500 mb-6">{t("co.emptyText")}</p>
        <Link to="/" data-testid="empty-cart-shop" className="inline-block bg-neon-green text-black font-anton uppercase px-6 py-3 glow-green">{t("co.seeShops")}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h1 className="font-anton text-4xl uppercase text-white">Checkout <span className="neon-text-green">Ultra-Fast</span></h1>
      </div>
      <p className="text-zinc-500 text-sm mb-8">{t("co.vendor")} : <span className="text-neon-magenta font-semibold">{vendor?.name}</span> — {vendor?.city}</p>

      {/* Items */}
      <div className="space-y-3 mb-8">
        {unavailable.length > 0 && (
          <div data-testid="cart-unavailable" className="border border-red-500/60 bg-red-500/10 p-3 text-sm">
            {unavailable.map((x) => (
              <p key={x.product_id} className="text-red-300" data-testid="cart-unavailable-item">
                {x.gone ? `❌ ${x.name} — ${t("co.unavailable")}` : `⚠️ ${x.name} — ${t("co.stockShort")} ${x.available} (quantité ajustée)`}
              </p>
            ))}
          </div>
        )}
        {items.map((i) => (
          <div key={i.key || i.product_id} data-testid="cart-item" className="flex items-center gap-4 bg-ink-surface border border-zinc-800 p-3">
            <img src={imageUrl(i.image_url) || PLACEHOLDER} alt={i.name} className="h-16 w-16 object-cover" onError={(e) => { e.target.src = PLACEHOLDER; }} />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-white text-sm leading-tight line-clamp-2">{i.name}</h3>
              <span className="text-neon-green font-anton">{i.price.toFixed(2)}€</span>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="qty-minus" onClick={() => setQty(i.key || i.product_id, i.qty - 1)} className="h-10 w-10 flex items-center justify-center border border-zinc-700 text-white hover:border-neon-green"><Minus className="h-4 w-4" /></button>
              <span className="w-6 text-center text-white font-bold" data-testid="qty-value">{i.qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty(i.key || i.product_id, i.qty + 1)} className="h-10 w-10 flex items-center justify-center border border-zinc-700 text-white hover:border-neon-green"><Plus className="h-4 w-4" /></button>
            </div>
            <button data-testid="remove-item" onClick={() => removeItem(i.key || i.product_id)} className="text-zinc-500 hover:text-red-500 p-2"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      {/* Mode de livraison */}
      <div className={`grid grid-cols-1 ${isNational ? "" : "sm:grid-cols-2"} gap-3 mb-8`} data-testid="delivery-mode-block">
        {!isNational && <div className="border p-4 border-neon-green bg-neon-green/5" data-testid="delivery-mode-express">
          <div className="flex items-center justify-between">
            <p className="text-white font-anton uppercase text-sm">🛵 Livreur Express</p>
            {!vendor?.is_national && <span className="bg-neon-green text-black text-[10px] font-bold uppercase px-2 py-0.5">Recommandé</span>}
          </div>
          <p className="text-zinc-400 text-xs mt-1">Livreur dédié géolocalisé en direct. Remise en main propre avec code de réception.</p>
          {!vendor?.is_national ? (
            <p className="text-neon-green text-xs font-bold mt-2">Estimé : {vendor?.delivery_time || "< 2h"}</p>
          ) : (
            <Link to="/" className="text-neon-green text-xs underline mt-2 inline-block">Voir les villes en express →</Link>
          )}
        </div>}
        <div className={`border p-4 ${isNational ? "border-neon-green bg-neon-green/5" : "border-zinc-800 bg-ink-surface opacity-70"}`} data-testid="delivery-mode-postal">
          <div className="flex items-center justify-between">
            <p className="text-white font-anton uppercase text-sm">📦 Colis Discret 24/48h</p>
            {isNational && <span className="bg-neon-green text-black text-[10px] font-bold uppercase px-2 py-0.5">Recommandé</span>}
          </div>
          <p className="text-zinc-400 text-xs mt-1">Enveloppe neutre, scellée, sans odeur. Aucune mention CBD sur l'étiquette extérieure.</p>
          {vendor?.is_national ? (
            <p className="text-neon-green text-xs font-bold mt-2">France entière · 24/48h</p>
          ) : (
            <Link to="/boutique/national" className="text-neon-magenta text-xs underline mt-2 inline-block">Commander en colis discret →</Link>
          )}
        </div>
      </div>

      {/* Delivery location + breakdown */}
      <div className="border-t border-zinc-800 pt-4 mb-2">
        <button
          type="button"
          data-testid="checkout-locate"
          onClick={locate}
          disabled={locating}
          className="w-full flex items-center justify-center gap-2 border border-neon-green text-neon-green uppercase text-sm font-bold py-3 mb-4 hover:bg-neon-green hover:text-black transition-colors disabled:opacity-50"
        >
          {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
          {coords ? t("co.located") : t("co.locate")}
        </button>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between text-zinc-300"><span>{t("co.subtotal")}</span><span data-testid="cart-subtotal">{subtotal.toFixed(2)}€</span></div>
          <div className="flex justify-between text-zinc-300">
            <span className="flex items-center gap-1"><Truck className="h-4 w-4 text-neon-green" /> {t("co.delivery")} {distance != null ? `(${distance} km)` : ""}</span>
            <span data-testid="cart-delivery">{deliveryFee === 0 ? <span className="text-neon-green font-bold">{t("co.free")}</span> : `${deliveryFee.toFixed(2)}€`}</span>
          </div>
          <FreeDeliveryBar subtotal={subtotal} threshold={effectiveThreshold} club={clubActive} />
          <CartCrossSell vendorId={vendor?.id} items={items} left={effectiveThreshold - subtotal}
            onAdd={(p) => { if (addItem(p, vendor)) toast.success(`${p.name} ${t("store.added")}`, { style: { background: "#121212", color: "#9dff00", border: "1px solid #9dff00" } }); }} />
          {clubFree && (
            <div className="flex justify-between text-neon-green font-semibold" data-testid="club-free-line">
              <span className="flex items-center gap-1"><Crown className="h-4 w-4" /> {t("co.clubActive")}</span>
              <span>0.00€</span>
            </div>
          )}
          {clubFee > 0 && (
            <div className="flex justify-between text-neon-magenta font-semibold" data-testid="club-fee-line">
              <span className="flex items-center gap-1"><Crown className="h-4 w-4" /> {t("co.clubFeeLine")}</span>
              <span>+ 5.99€</span>
            </div>
          )}
          {discount > 0 && (
            <div className="flex justify-between text-neon-magenta font-semibold">
              <span>{t("co.discount")} {promo?.code}{promo?.free_delivery ? " · livraison offerte" : ""}</span>
              <span data-testid="cart-discount">- {discount.toFixed(2)}€</span>
            </div>
          )}
          {promo?.gift_label && (
            <div className="flex justify-between text-neon-green font-semibold" data-testid="cart-promo-gift">
              <span>🎁 {promo.gift_label}</span>
              <span>0.00€</span>
            </div>
          )}
          {refDiscount > 0 && (
            <div className="flex justify-between text-neon-green font-semibold" data-testid="referral-discount-line">
              <span>🤝 {t("co.refLine")} {referral.code}</span>
              <span>- {refDiscount.toFixed(2)}€</span>
            </div>
          )}
          {refCreditApplied > 0 && (
            <div className="flex justify-between text-neon-green font-semibold" data-testid="referral-credit-line">
              <span>🎁 {t("co.refCreditLine")}</span>
              <span>- {refCreditApplied.toFixed(2)}€</span>
            </div>
          )}
        </div>

      </div>

      <div className="flex justify-between items-center border-t border-zinc-800 pt-4 mb-2">
        <span className="text-zinc-400 uppercase font-semibold">{t("co.total")}</span>
        <span className="font-anton text-3xl text-neon-green" data-testid="cart-total">{grandTotal.toFixed(2)}€</span>
      </div>
      <p className="text-neon-magenta text-xs font-semibold mb-8" data-testid="points-preview">{t("co.points1")} {pointsEarned} {pointsEarned > 1 ? t("co.points2pl") : t("co.points2")}</p>

      {/* Form */}
      <form onSubmit={submit} className="space-y-4">
        <h2 className="font-anton text-2xl uppercase text-white">{t("co.infoTitle")}</h2>
        <p className="text-zinc-500 text-xs -mt-2" data-testid="checkout-pay-hint">{isNational ? "Paiement par virement instantané après confirmation — RIB affiché à l'étape suivante." : acceptsCard ? "Paiement à la remise : carte sans contact ou espèces. Rien à payer en ligne." : "Paiement en espèces à la remise. Rien à payer en ligne."}</p>
        <div>
          <input data-testid="checkout-name" data-field="customer_name" value={form.customer_name} onChange={(e) => { markStarted(); upd("customer_name", e.target.value); if (errors.customer_name) setErrors((x) => ({ ...x, customer_name: undefined })); }} placeholder={t("co.name")} aria-invalid={!!errors.customer_name} className={`w-full bg-ink-surface border text-white px-4 py-3 focus:outline-none focus:border-neon-green ${errors.customer_name ? "border-red-500" : "border-zinc-700"}`} />
          {errors.customer_name && <p data-testid="error-customer_name" className="text-red-400 text-xs mt-1">⚠ {errors.customer_name}</p>}
        </div>
        <div>
          <input data-testid="checkout-phone" data-field="customer_phone" value={form.customer_phone} onChange={(e) => { markStarted(); upd("customer_phone", e.target.value); if (errors.customer_phone) setErrors((x) => ({ ...x, customer_phone: undefined })); }} aria-invalid={!!errors.customer_phone} placeholder={t("co.phone")} className={`w-full bg-ink-surface border ${errors.customer_phone ? "border-red-500" : "border-zinc-700"} text-white px-4 py-3 focus:outline-none focus:border-neon-green`} />
          {errors.customer_phone && <p data-testid="error-customer_phone" className="text-red-400 text-xs mt-1">⚠ {errors.customer_phone}</p>}
        </div>
        <div className="relative">
          <input data-testid="checkout-address" data-field="customer_address" value={form.customer_address} onChange={(e) => { markStarted(); upd("customer_address", e.target.value); if (errors.customer_address) setErrors((x) => ({ ...x, customer_address: undefined })); }} aria-invalid={!!errors.customer_address} onBlur={() => setTimeout(() => setSuggestions([]), 200)} placeholder={t("co.address")} autoComplete="off" className={`w-full bg-ink-surface border ${errors.customer_address ? "border-red-500" : "border-zinc-700"} text-white px-4 py-3 focus:outline-none focus:border-neon-green`} />
          {errors.customer_address && <p data-testid="error-customer_address" className="text-red-400 text-xs mt-1">⚠ {errors.customer_address}</p>}
          {suggestions.length > 0 && (
            <ul data-testid="addr-suggestions" className="absolute z-20 left-0 right-0 top-full bg-ink-surface border border-neon-green/60 shadow-lg max-h-56 overflow-y-auto">
              {suggestions.map((s, i) => (
                <li key={s.label}>
                  <button type="button" data-testid="addr-suggestion-item" onMouseDown={() => pickSuggestion(s)} className="w-full text-left px-4 py-2.5 text-sm text-zinc-300 hover:bg-neon-green hover:text-black transition-colors border-b border-zinc-800 last:border-b-0">
                    📍 {s.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {form.customer_address.trim().length >= 10 && addrStatus !== "idle" && (
          <div data-testid="addr-status" className="text-xs -mt-2">
            {addrStatus === "loading" && <span className="text-zinc-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> {t("co.addrChecking")}</span>}
            {addrStatus === "ok" && <span className="text-neon-green">📍 {t("co.addrOk")} {addrLabel}{distance != null ? ` — ${distance} km · ${t("co.delivery")} ${deliveryFee === 0 ? t("co.free") : deliveryFee.toFixed(2) + "€"}` : ""}</span>}
            {addrStatus === "error" && <span className="text-yellow-400">⚠ {t("co.addrErr")}</span>}
          </div>
        )}


        {!isNational && (
        <input
          data-testid="checkout-door-code"
          value={form.door_code}
          onChange={(e) => upd("door_code", e.target.value)}
          placeholder="Digicode / Bâtiment / Étage (ex: B4209, Bât B, 3e étage)"
          className="w-full bg-ink-surface border border-zinc-700 text-white px-4 py-3 text-sm focus:outline-none focus:border-neon-green"
          />
        )}

        {/* Options repliables */}
        <div className="border border-zinc-800 bg-ink-surface" data-testid="checkout-options">
          <button type="button" data-testid="checkout-options-toggle" onClick={() => setOptionsOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left">
            <span className="font-anton uppercase text-white text-sm">{optionsOpen ? "−" : "+"} Options <span className="text-zinc-500 font-outfit text-xs normal-case">(code promo, parrain, Club{isNational ? "" : ", regroupement, consignes"}, note)</span></span>
            {optionsSummary && <span className="text-[10px] uppercase font-bold text-neon-green">{optionsSummary}</span>}
          </button>
          {optionsOpen && (
            <div className="px-4 pb-4 space-y-4">
        {/* Promo code */}
        <div className="mt-4">
          {promo && (discount > 0 || promo.free_delivery) ? (
            <div className="flex items-center justify-between border border-neon-magenta/50 bg-neon-magenta/10 px-3 py-2 text-sm">
              <span className="text-neon-magenta font-bold" data-testid="promo-applied">✓ {promo.code} {promo.free_delivery ? `(livraison offerte${promo.gift_label ? ` + 🎁 ${promo.gift_label}` : ""})` : `(-${discount.toFixed(2)}€)`}</span>
              <button type="button" data-testid="promo-remove" onClick={clearPromo} className="text-zinc-400 hover:text-red-400 text-xs uppercase">{t("co.remove")}</button>
            </div>
          ) : referral ? (
            <p className="text-zinc-500 text-xs" data-testid="promo-blocked">🚫 {t("co.noCumulPromo")}</p>
          ) : (
            <div className="flex gap-2">
              <input data-testid="promo-input" value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder={t("co.promoPh")} className="flex-1 min-w-0 bg-ink-surface border border-zinc-700 text-white px-3 py-2 text-sm focus:outline-none focus:border-neon-magenta" />
              <button type="button" data-testid="promo-apply" onClick={applyPromo} disabled={promoLoading || !promoInput.trim()} className="shrink-0 bg-neon-magenta text-white uppercase text-xs font-bold px-3 disabled:opacity-40">{promoLoading ? "..." : t("co.apply")}</button>
            </div>
          )}
        </div>

        {/* Code parrain */}
        <div className="mt-2">
          {referral ? (
            <div className="flex items-center justify-between border border-neon-green/50 bg-neon-green/10 px-3 py-2 text-sm">
              <span className="text-neon-green font-bold" data-testid="referral-applied">🤝 {referral.code} (-5.00€)</span>
              <button type="button" data-testid="referral-remove" onClick={() => { setReferral(null); setRefInput(""); }} className="text-zinc-400 hover:text-red-400 text-xs uppercase">{t("co.remove")}</button>
            </div>
          ) : refCreditApplied > 0 ? (
            <p className="text-neon-green text-xs" data-testid="referral-credit-info">🎁 {t("co.refCreditInfo")}</p>
          ) : refCreditBlocked ? (
            <p className="text-zinc-500 text-xs" data-testid="referral-credit-blocked">🎁 {t("co.refCreditBlocked")}</p>
          ) : promo ? (
            <p className="text-zinc-500 text-xs" data-testid="referral-blocked">🚫 {t("co.noCumulRef")}</p>
          ) : (
            <div className="flex gap-2">
              <input data-testid="referral-input" value={refInput} onChange={(e) => setRefInput(e.target.value.toUpperCase())} placeholder={t("co.refPh")} className="flex-1 min-w-0 bg-ink-surface border border-zinc-700 text-white px-3 py-2 text-sm focus:outline-none focus:border-neon-green" />
              <button type="button" data-testid="referral-apply" onClick={applyReferral} disabled={refLoading || !refInput.trim()} className="shrink-0 bg-neon-green text-black uppercase text-xs font-bold px-3 disabled:opacity-40">{refLoading ? "..." : t("co.apply")}</button>
            </div>
          )}
        </div>

        {/* Club STUFF */}
        {!clubActive && (
          <label data-testid="club-optin" className={`mt-4 flex items-start gap-3 border p-4 cursor-pointer transition-all ${clubOptIn ? "border-neon-green bg-neon-green/5" : "border-zinc-700 hover:border-neon-green/60"}`}>
            <input type="checkbox" data-testid="club-optin-checkbox" checked={clubOptIn} onChange={(e) => setClubOptIn(e.target.checked)} className="mt-1 accent-[#9dff00] h-4 w-4" />
            <span className="text-sm">
              <span className="flex items-center gap-1.5 text-white font-bold"><Crown className="h-4 w-4 text-neon-green" /> {t("co.clubTitle")} <span className="text-neon-magenta">+5,99€/mois</span></span>
              <span className="text-zinc-400 block mt-0.5">{t("co.clubText")}</span>
              <Link to="/club" onClick={(e) => e.stopPropagation()} className="text-neon-green text-xs underline">{t("co.clubMore")}</Link>
            </span>
          </label>
        )}
        {clubActive && club?.paid_until && (
          <div data-testid="club-member-badge" className="mt-4 flex items-center gap-2 border border-neon-green/50 bg-neon-green/5 px-4 py-3 text-sm text-neon-green">
            <Crown className="h-4 w-4" /> {t("co.clubMember")} {new Date(club.paid_until).toLocaleDateString()}
          </div>
        )}

        {/* Regroupement de commandes → petit cadeau */}
        {!isNational && (<label data-testid="grouping-optin" className={`mt-4 flex items-start gap-3 border p-4 cursor-pointer transition-all ${groupingOk ? "border-neon-magenta bg-neon-magenta/5" : "border-zinc-700 hover:border-neon-magenta/60"}`}>
          <input type="checkbox" data-testid="grouping-checkbox" checked={groupingOk} onChange={(e) => setGroupingOk(e.target.checked)} className="mt-1 accent-[#c026d3] h-4 w-4" />
          <span className="text-sm">
            <span className="flex items-center gap-1.5 text-white font-bold"><Gift className="h-4 w-4 text-neon-magenta" /> {t("co.groupTitle")}</span>
            <span className="text-zinc-400 block mt-0.5">{t("co.groupText")}</span>
          </span>
        </label>)}

        {outOfZone && (
          <div data-testid="out-of-zone-warning" className="flex items-start gap-2 border border-yellow-500/50 bg-yellow-500/10 text-yellow-400 text-xs px-3 py-2 mt-3">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{t("co.outzone1")} <b>{t("co.outzone2")}</b> {t("co.outzone3")} ({distance} km &gt; {radius} km). {t("co.outzone4")} <b>{t("co.outzone5")}</b> {t("co.outzone6")}</span>
          </div>
        )}
        <textarea data-testid="checkout-notes" value={form.notes} onChange={(e) => upd("notes", e.target.value)} placeholder={t("co.notes")} rows={2} className="w-full bg-ink-surface border border-zinc-700 text-white px-4 py-3 focus:outline-none focus:border-neon-green" />
        {/* Consignes coursier */}
        {!isNational && (<div className="border border-zinc-800 bg-ink-surface p-4 space-y-3" data-testid="courier-instructions-block">
          <p className="font-anton uppercase text-white text-sm">📦 Consignes pour le livreur <span className="text-zinc-500 font-outfit text-xs normal-case">(optionnel)</span></p>
          <div className="flex flex-wrap gap-2">
            {["Appeler à l'arrivée", "Sonner à l'interphone", "RDV devant l'immeuble"].map((opt) => (
              <button
                key={opt}
                type="button"
                data-testid="courier-instruction-chip"
                onClick={() => setInstruction(instruction === opt ? "" : opt)}
                className={`text-xs font-bold px-3 py-2 border transition-colors ${instruction === opt ? "border-neon-green bg-neon-green/10 text-neon-green" : "border-zinc-700 text-zinc-400 hover:border-neon-green/60"}`}
              >{opt}</button>
            ))}
          </div>
          <p className="text-zinc-600 text-[11px]">Ces infos sont transmises uniquement à ton livreur (via WhatsApp/Snapchat) pour faciliter la remise en main propre.</p>
        </div>)}
            </div>
          )}
        </div>

        {/* Règlement en main propre */}
        {isNational ? (
        <div className="border border-neon-green/60 bg-neon-green/5 p-4 space-y-2" data-testid="payment-method-block">
          <p className="font-anton uppercase text-white text-sm">🏦 Paiement par virement instantané</p>
          <p className="text-zinc-400 text-xs">Après confirmation, tu reçois le RIB STUFF, le montant exact et une référence à mettre en libellé. Ton colis part dès réception du virement (quelques minutes avec un virement instantané). Sans paiement sous {nationalPay?.cancel_hours || 48}h, la commande est annulée automatiquement.</p>
        </div>
        ) : (
        <div className="border border-zinc-800 bg-ink-surface p-4 space-y-3" data-testid="payment-method-block">
          <p className="font-anton uppercase text-white text-sm">🤝 Paiement à la remise{acceptsCard ? " : carte sans contact ou espèces" : " : espèces"}</p>
          <p className="text-zinc-500 text-xs">{acceptsCard ? "Tu règles ton livreur à la remise du colis : carte sans contact sur son terminal (Tap to Pay) ou espèces." : "Tu règles ton livreur en espèces à la remise du colis."}</p>
          {acceptsCard && (
            <div className="grid grid-cols-2 gap-2" data-testid="payment-choice">
              <button type="button" data-testid="pay-card" onClick={() => setPayMethod("card")}
                className={`flex flex-col items-start gap-0.5 border p-3 text-left transition-colors ${payMethod === "card" ? "border-blue-400 bg-blue-400/10" : "border-zinc-700 hover:border-blue-400/60"}`}>
                <span className={`text-sm font-bold ${payMethod === "card" ? "text-blue-400" : "text-white"}`}>💳 Carte</span>
                <span className="text-zinc-500 text-[11px]">Sans contact sur le terminal du livreur</span>
              </button>
              <button type="button" data-testid="pay-cash" onClick={() => setPayMethod("cash")}
                className={`flex flex-col items-start gap-0.5 border p-3 text-left transition-colors ${payMethod === "cash" ? "border-neon-green bg-neon-green/10" : "border-zinc-700 hover:border-neon-green/60"}`}>
                <span className={`text-sm font-bold ${payMethod === "cash" ? "text-neon-green" : "text-white"}`}>💶 Espèces</span>
                <span className="text-zinc-500 text-[11px]">Appoint ou rendu de monnaie</span>
              </button>
            </div>
          )}
          {acceptsCard && payMethod === "card" ? (
            <p data-testid="card-info" className="text-blue-300 text-xs border border-blue-400/40 bg-blue-400/5 px-3 py-2">💳 Le paiement par carte sera réalisé <span className="font-bold">auprès du livreur</span>, sans contact, sur son téléphone (Tap to Pay) au moment de la remise. Rien n'est débité maintenant.</p>
          ) : (
          <div data-testid="cash-appoint-block">
            <p className="text-zinc-400 text-xs uppercase font-bold mb-2">Prévoir l'appoint / rendu de monnaie sur :</p>
            <div className="flex flex-wrap gap-2">
              {["Montant exact", "Billet de 20€", "Billet de 50€", "Billet de 100€"].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  data-testid="cash-amount-chip"
                  onClick={() => setCashAmount(cashAmount === opt ? "" : opt)}
                  className={`text-xs font-bold px-3 py-2 border transition-colors ${cashAmount === opt ? "border-neon-magenta bg-neon-magenta/10 text-neon-magenta" : "border-zinc-700 text-zinc-400 hover:border-neon-magenta/60"}`}
                >{opt}</button>
              ))}
            </div>
            {cashAmount && cashAmount !== "Montant exact" && <p className="text-neon-magenta text-xs mt-2">Le livreur préparera la monnaie sur {cashAmount.toLowerCase()} (total : {grandTotal.toFixed(2)}€).</p>}
          </div>
          )}
        </div>
        )}

        {/* Channel */}
        {!isNational && <p className="font-anton uppercase text-white text-sm -mb-1" data-testid="channel-title">📲 Ton livreur te contacte sur</p>}
        <div className="flex gap-3">
          <button type="button" data-testid="channel-whatsapp" onClick={() => setChannel("whatsapp")} className={`flex-1 flex items-center justify-center gap-2 py-3 border uppercase text-sm font-bold transition-all ${channel === "whatsapp" ? "bg-neon-green text-black border-neon-green" : "border-zinc-700 text-zinc-400"}`}>
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </button>
          <button type="button" data-testid="channel-snapchat" onClick={() => setChannel("snapchat")} className={`flex-1 flex items-center justify-center gap-2 py-3 border uppercase text-sm font-bold transition-all disabled:opacity-30 ${channel === "snapchat" ? "bg-[#FFFC00] text-black border-[#FFFC00]" : "border-zinc-700 text-zinc-400"}`}>
            <Ghost className="h-4 w-4" /> Snapchat
          </button>
        </div>
        {channel === "snapchat" && !isNational && (
          <input data-testid="snap-handle-input" data-field="snap_handle" value={snapHandle} onChange={(e) => setSnapHandle(e.target.value.replace(/^@/, ""))} placeholder="Ton pseudo Snapchat * (le livreur t'ajoute)" maxLength={40} required
            className={`w-full bg-ink-surface border text-white px-4 py-3 text-sm focus:outline-none focus:border-[#FFFC00] ${errors.snap_handle ? "border-red-500" : "border-[#FFFC00]/60"}`} />
        )}
        {errors.snap_handle && <p data-testid="error-snap_handle" className="text-red-400 text-xs -mt-2">⚠ {errors.snap_handle}</p>}

        <div className="sticky bottom-0 z-30 -mx-4 px-4 pt-3 pb-safe bg-gradient-to-t from-ink via-ink/95 to-transparent md:static md:mx-0 md:px-0 md:pt-0 md:pb-0 md:bg-none">
          <button type="submit" data-testid="checkout-submit" disabled={submitting} className="w-full flex items-center justify-center gap-2 bg-neon-green text-black font-anton uppercase text-base sm:text-lg py-4 glow-green hover:bg-neon-greenDark transition-colors disabled:opacity-50">
            <MessageCircle className="h-5 w-5 shrink-0" /> {submitting ? t("co.sending") : isNational ? `Confirmer la commande (${grandTotal.toFixed(2)}€)` : `Confirmer et lancer la livraison (${grandTotal.toFixed(2)}€)`}
          </button>
        </div>
        <div className="space-y-1.5 pt-1" data-testid="checkout-reassurance">
          {[isNational ? "🏦 Paiement par virement instantané — RIB affiché après confirmation" : acceptsCard ? "💳 Paiement à la remise : carte sans contact ou espèces" : "💶 Paiement en espèces à la remise", "📦 Paquet 100% opaque & hermétique — sachet scellé anti-odeur", isNational ? "🚚 Expédition sous 24/48h partout en France dès réception du virement" : "🔐 Remise en main propre avec code de réception à 4 chiffres", "🛡️ Conforme loi française · THC < 0,3% · Réservé aux majeurs (18+)"].map((line) => (
            <p key={line} className="text-zinc-500 text-xs flex items-start gap-1.5">{line}</p>
          ))}
        </div>
      </form>
    </div>
  );
}
